/**
 * VAT invoice opt-in fields in the cart (company name + Polish NIP).
 *
 * Values are persisted as cart attributes on every keystroke (debounced), so they survive
 * the section re-render that follows any quantity change, and they reach the order even when
 * the customer checks out from the drawer.
 */
if (!customElements.get('cart-invoice-fields')) {
  customElements.define(
    'cart-invoice-fields',
    class CartInvoiceFields extends HTMLElement {
      static DEBOUNCE_MS = 400;
      static NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

      connectedCallback() {
        this.toggle = this.querySelector('.cart-invoice__checkbox');
        this.fields = this.querySelector('.cart-invoice__fields');
        this.flagInput = this.querySelector('[name="attributes[Faktura VAT]"]');
        this.companyInput = this.querySelector('[name="attributes[Nazwa firmy]"]');
        this.nipInput = this.querySelector('[name="attributes[NIP]"]');
        this.errorMessage = this.querySelector('.cart-invoice__error');
        this.form = document.getElementById(this.dataset.formId);

        if (!this.toggle || !this.nipInput) return;

        this.debouncedSave = this.debounce(() => this.save(), CartInvoiceFields.DEBOUNCE_MS);

        this.toggle.addEventListener('change', () => this.onToggle());
        this.companyInput.addEventListener('input', () => this.debouncedSave());
        this.nipInput.addEventListener('input', () => {
          this.clearError();
          this.debouncedSave();
        });
        this.nipInput.addEventListener('blur', () => this.validate({ flagEmpty: false }));

        if (this.form) {
          this.form.addEventListener('submit', (event) => {
            if (!this.validate({ flagEmpty: true })) {
              event.preventDefault();
              this.nipInput.focus();
            }
          });
        }
      }

      onToggle() {
        this.fields.hidden = !this.toggle.checked;
        this.flagInput.value = this.toggle.checked ? 'tak' : '';

        if (!this.toggle.checked) {
          this.companyInput.value = '';
          this.nipInput.value = '';
          this.clearError();
        }

        this.save();
      }

      /**
       * Returns false only when the customer asked for an invoice and the NIP is unusable.
       * An untouched empty field stays silent on blur — it only turns into an error on submit.
       */
      validate({ flagEmpty }) {
        if (!this.toggle.checked) return true;

        const isEmpty = this.nipInput.value.trim() === '';
        if (isEmpty && !flagEmpty) {
          this.clearError();
          return true;
        }

        const valid = CartInvoiceFields.isValidNip(this.nipInput.value);
        this.errorMessage.hidden = valid;
        this.nipInput.setAttribute('aria-invalid', valid ? 'false' : 'true');
        return valid;
      }

      clearError() {
        this.errorMessage.hidden = true;
        this.nipInput.setAttribute('aria-invalid', 'false');
      }

      save() {
        const body = JSON.stringify({
          attributes: {
            'Faktura VAT': this.flagInput.value,
            'Nazwa firmy': this.companyInput.value,
            NIP: this.nipInput.value,
          },
        });

        fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        }).catch(() => {
          // Silent: the inputs are also part of the cart form, so the values still reach checkout.
        });
      }

      /** Polish NIP: 10 digits with a modulo-11 checksum over the first nine. */
      static isValidNip(value) {
        const digits = String(value).replace(/[\s-]/g, '');
        if (!/^\d{10}$/.test(digits)) return false;

        const sum = CartInvoiceFields.NIP_WEIGHTS.reduce(
          (total, weight, index) => total + weight * Number(digits[index]),
          0
        );
        const checksum = sum % 11;

        return checksum !== 10 && checksum === Number(digits[9]);
      }

      debounce(fn, wait) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    }
  );
}
